import * as React from 'react';
import { observer } from 'mobx-react';
import { useStores } from 'store';
import { AdvanceTable, CheckboxField, notification } from 'ui-lib';
import { AccountType } from 'dto/access-management/account-dto';

import rolesPermissionCss from './roles-permissions.css';
import { getErrorMessage, isNullOrEmpty } from '../../../../common-utils';
import { IRole } from 'dto/access-management/user-role-dto';
import { useManageRolePermissionContext } from '.';

interface IDisplayLevelRoleMatrix {
    name: string;
    levels: { [key in AccountType]: boolean };
}
interface IRoleMatrix {
    roles: IRole[];
    roleNames: string[];
    accountTypes: AccountType[];
}
export const LevelsAndRolesMatrixPage = observer(() => {
    const { roleStore } = useStores();
    const { getDisplayRoleName } = useManageRolePermissionContext();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [roleMatrix, setRoleMatrix] = React.useState<IRoleMatrix>({
        roles: [],
        roleNames: [],
        accountTypes: [AccountType.DPU, AccountType.VAR, AccountType.CLIENT],
    });

    const dataSource = React.useMemo<IDisplayLevelRoleMatrix[]>(() => {
        const { roles, roleNames, accountTypes } = roleMatrix;
        if (isNullOrEmpty(roles) || isNullOrEmpty(roleNames) || isNullOrEmpty(accountTypes)) return [];
        return roleNames.map((name) => {
            return {
                name: name,
                levels: accountTypes.reduce((acc, accountType) => {
                    acc[accountType] = roles.some(
                        (role) => role.accountType === accountType && getDisplayRoleName(role, 'roleType') === name,
                    );
                    return acc;
                }, {} as { [key in AccountType]: boolean }),
            };
        });
    }, [roleMatrix]);

    const fetchData = async (): Promise<void> => {
        try {
            setLoading(true);
            const managedRoles = await roleStore.getManagedRoles();

            const uniqueRoleNames = new Set<string>();
            const uniqueAccountTypes = new Set<AccountType>();
            managedRoles.forEach((role) => {
                uniqueRoleNames.add(getDisplayRoleName(role, 'roleType'));
                uniqueAccountTypes.add(role.accountType);
            });
            const roleNamesArr = Array.from(uniqueRoleNames);
            const uniqAccountTypes = Array.from(uniqueAccountTypes);
            setRoleMatrix({
                roles: managedRoles,
                roleNames: roleNamesArr,
                accountTypes: uniqAccountTypes,
            });
        } catch (error) {
            notification.error({
                message: 'Error',
                description: getErrorMessage(error),
            });
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchData();
    }, []);

    return (
        <AdvanceTable
            className={rolesPermissionCss.levelMatrixTable}
            loadingState={loading}
            title={''}
            table={{
                columns: [
                    {
                        code: 'rowHead',
                        title: 'Role',
                        dataIndex: 'name',
                        rowScope: 'name',
                    },
                    ...roleMatrix.accountTypes.map((accountType) => {
                        return {
                            code: accountType,
                            title: accountType,
                            dataIndex: accountType,
                            render: (_, record: IDisplayLevelRoleMatrix) => {
                                const checked = record.levels[accountType];
                                return <CheckboxField label={checked ? record.name : ''} checked={checked} />;
                            },
                        };
                    }),
                ],
                dataSource: dataSource,
                rowKey: 'name',
                extraOptions: {
                    pagination: {
                        hideOnSinglePage: true,
                    },
                },
            }}
        />
    );
});
